/** Cookie-consent helpers. Storage: localStorage["cookie_consent"] = JSON
 *  { version, decision: "all"|"reject"|"custom", method: "click"|"GPC",
 *    categories: { necessary, analytics, marketing }, at }.
 *
 *  Strictly-necessary cookies are always true. Other categories default to
 *  false until consent.  GPC users get an auto-rejection that opts them out
 *  of analytics + marketing without showing the banner.
 *
 *  Backend persistence: every decision is POSTed to /api/me/consent. The
 *  backend ties it to the user_id when a JWT is present, otherwise to an
 *  anonymous_id we also persist locally — satisfies the GDPR / CPRA audit
 *  trail requirement.
 */
import api from "./api";

export const CONSENT_VERSION = "1.0";
export const STORAGE_KEY = "cookie_consent";
export const ANON_KEY = "br_anon_id";
export const OPEN_PREFS_EVENT = "br:open-cookie-prefs";

function getAnonId() {
  try {
    let v = localStorage.getItem(ANON_KEY);
    if (!v) {
      v = (crypto?.randomUUID?.() || (Date.now() + "-" + Math.random().toString(36).slice(2)));
      localStorage.setItem(ANON_KEY, v);
    }
    return v;
  } catch { return null; }
}

export function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c?.version !== CONSENT_VERSION) return null; // re-prompt on version bump
    return c;
  } catch {
    return null;
  }
}

export function writeConsent(decision, { method = "click", analytics = false, marketing = false } = {}) {
  const payload = {
    version: CONSENT_VERSION,
    decision,
    method,
    categories: { necessary: true, analytics, marketing },
    at: new Date().toISOString(),
  };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
  // Surface to the rest of the app (e.g. an analytics loader can listen).
  window.dispatchEvent(new CustomEvent("br:consent-changed", { detail: payload }));
  // Persist server-side for audit (fire-and-forget).
  try {
    api.post("/me/consent", {
      decision, method, categories: payload.categories,
      anonymous_id: getAnonId(),
    }).catch(() => {});
  } catch {}
  return payload;
}

export function clearConsent() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

/** GPC signal — Sec. of State CCPA enforcement, CPA, CTDPA, TDPSA, OCPA. */
export function isGPC() {
  return typeof navigator !== "undefined" && navigator.globalPrivacyControl === true;
}

/** Open the preferences modal from anywhere (e.g. footer link). */
export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT));
}
